import React from "react";
import {
  AlertTriangle, ArrowDownRight, ArrowUpRight, Ban, Clock3, Eye, Hourglass, Minus, MinusCircle,
  TrendingDown, TrendingUp,
} from "lucide-react";
import { isNum, num, pts, words } from "./fmt";
import { EngineStateTag, useEngineState } from "../ui/EngineState";

/*
 * VERDICT — the one answer on the page (2026-09-28), now the pure readers and
 * the two bodies (order ticket / why not) that the Buyer Funnel renders.
 *
 * ONE verdict, built in the order the research earned: magnitude first (can
 * the move pay for the premium?), direction second (which side?). Reasons are
 * merged and de-duplicated, and there is exactly one "what would change this"
 * sentence.
 *
 * 2026-10-09: the wording is keyed on the ACTUAL blocker. `edge.verdict.edge`
 * is false for ANY blocker (kill switch, opening noise, past cutoff, no chain,
 * expiry tail) — it used to read as "market too quiet" every time. And the
 * ticket's exits come from the ACTIVE rule for the side when there is one:
 * the direction engine's own plan (a ~4% stop, 1.5R target, 5-minute time
 * stop) is the exit style the research found loses for a buyer.
 */

const INK = { emerald: "text-emerald-300", rose: "text-rose-300", amber: "text-amber-300", slate: "text-slate-300" };

/* Blocker codes → plain words. Magnitude codes are the only ones that may say "quiet". */
export const MAGNITUDE_BLOCKERS = new Set(["NO_TAIL", "PREMIUM_NOT_COVERED"]);
export const BLOCKER_WORDS = {
  NO_TAIL: "market is not moving enough",
  PREMIUM_NOT_COVERED: "the move can't pay the option's costs",
  OPENING_NOISE: "opening minutes — let the range settle",
  PAST_ENTRY_CUTOFF: "past the entry cutoff — no new trades",
  KILL_SWITCH: "kill switch is ON",
  EXPIRY_TAIL: "expiry afternoon — no new buys",
  NO_CHAIN: "no live option chain",
  MARKET_CLOSED: "market closed",
  MARKET_HOLIDAY: "market holiday",
  WEEKEND: "weekend",
  MIDDAY_THETA: "midday — slow market, time decay bites",
  EXPIRY_DAY: "expiry day",
  EVENT_DAY: "scheduled event today",
  GATE_UNKNOWN: "movement gate not measured yet",
  TARGET_UNLIKELY: "move covers costs, the +target is unlikely",
  WIDE_SPREAD_CE: "call spread too wide",
  WIDE_SPREAD_PE: "put spread too wide",
  THETA_HEAVY_CE: "call decays too fast today",
  THETA_HEAVY_PE: "put decays too fast today",
};
export const blockerWords = (code) => BLOCKER_WORDS[code] || words(code).toLowerCase();

/* The first blocker that is NOT about magnitude, if any (session / safety / data). */
export function primaryBlocker(edge) {
  const list = edge?.verdict?.blockers || [];
  return list.find((b) => !MAGNITUDE_BLOCKERS.has(b)) || list[0] || null;
}

/* Direction-only reading of one index (used for the index switch). */
export function directionVerdict(side, sessionClosed = false) {
  if (sessionClosed) return { word: "CLOSED", tone: "slate" };
  if (side === "CALL") return { word: "BUY CALL", tone: "emerald" };
  if (side === "PUT") return { word: "BUY PUT", tone: "rose" };
  if (side === "AVOID") return { word: "AVOID", tone: "rose" };
  return { word: "WAIT", tone: "amber" };
}

/* Can the expected move pay for the cheaper side? From the side blocks, not the
 * overall verdict (which also carries session / safety / data blockers). */
export function payState(edge) {
  const sides = [edge?.call, edge?.put].filter((s) => s?.available);
  if (!sides.length) return null;
  if (sides.some((s) => s.can_profit === true)) return "profit";
  if (sides.some((s) => s.can_breakeven === true)) return "breakeven";
  return "none";
}

/* The single verdict: magnitude gates first, then direction picks the side. */
export function combinedVerdict(edge, view) {
  const closed = Boolean(view?.sessionClosed);
  const side = view?.action?.side;
  const edgeOk = edge?.verdict?.edge;              // true | false | undefined (not computed)
  if (closed) {
    const label = view?.closedLabel === "Market holiday" ? "Market holiday" : "Market closed";
    return { word: "NO TRADE", qualifier: `${label} · last snapshot`, tone: "slate", Icon: Clock3, go: false };
  }
  if (edgeOk === false) {
    const first = primaryBlocker(edge);
    const magnitude = !first || MAGNITUDE_BLOCKERS.has(first);
    const qualifier = magnitude
      ? (first === "PREMIUM_NOT_COVERED" ? "Move too small to pay the option's costs" : "Market too quiet to pay for the option")
      : blockerWords(first).replace(/^./, (c) => c.toUpperCase());
    return { word: "NO TRADE", qualifier, tone: magnitude ? "rose" : "amber", Icon: magnitude ? Ban : Clock3, go: false, blocker: first };
  }
  if (side === "AVOID") return { word: "NO TRADE", qualifier: "Conditions hostile", tone: "rose", Icon: Ban, go: false };
  if (side === "CALL" || side === "PUT") {
    const call = side === "CALL";
    const confirmed = edgeOk === true;
    // No edge payload yet: a BUY word without the magnitude gate would be the
    // direction engine alone, which is right ~50-55% of the time.
    if (edgeOk === undefined) {
      return { word: "WAIT", qualifier: `Price leans ${call ? "up" : "down"} · movement gate not measured yet`, tone: "amber", Icon: Hourglass, go: false };
    }
    return {
      word: call ? "BUY CALL" : "BUY PUT",
      qualifier: confirmed ? `Buy ${call ? "CE" : "PE"} · market moving enough` : "Market movement not confirmed · use smaller size",
      tone: confirmed ? (call ? "emerald" : "rose") : "amber",
      Icon: call ? TrendingUp : TrendingDown,
      go: Boolean(view?.tradePlan),
    };
  }
  if (edgeOk === true) return { word: "WAIT", qualifier: "Market moving · waiting for a side", tone: "amber", Icon: Hourglass, go: false };
  return { word: "NO TRADE", qualifier: "Waiting for a confirmed setup", tone: "amber", Icon: MinusCircle, go: false };
}

const norm = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/* Edge blockers/warnings + direction blockers, merged and de-duplicated, in
 * plain words. The "market closed" reason is dropped: the verdict's qualifier
 * already says it. */
export function mergedReasons(edge, view) {
  const v = edge?.verdict || {};
  const failedRequired = (view?.checklist || []).filter((c) => c.required && !c.pass).map((c) => c.label);
  const dirBlockers = failedRequired.length ? failedRequired : (view?.riskFlags || []);
  const out = [];
  const seen = new Set();
  const add = (text, tone) => {
    const k = norm(text);
    if (!k || seen.has(k) || k.includes("market closed") || k.includes("market holiday")) return;
    seen.add(k);
    out.push({ text, tone });
  };
  (v.blockers || []).forEach((b) => add(blockerWords(b), "rose"));
  dirBlockers.forEach((b) => add(words(b).toLowerCase(), "rose"));
  (v.warnings || []).forEach((w) => add(blockerWords(w), "amber"));
  return out;
}

/* Exactly one "what would turn this into a trade" sentence. */
export function nextStep(edge, view) {
  if (view?.sessionClosed && view?.closedLabel === "Market holiday") return "Market holiday — no session today. Every value here is the last snapshot, for planning only.";
  if (view?.sessionClosed) return "Nothing until the session opens — every value here is the last snapshot, for planning only.";
  if (view?.health?.ready === false) return "A required data feed is stale — nothing arms until spot, chain, IV and 1-minute candles are all fresh.";
  const first = primaryBlocker(edge);
  if (edge?.verdict?.edge === false && first && !MAGNITUDE_BLOCKERS.has(first)) {
    const s = edge?.session || {};
    if (first === "OPENING_NOISE") return `Entries open after the opening minutes${s.windows ? ` (${(s.windows.find((w) => w.key === "opening_noise") || {}).end || ""})` : ""} — let the first range settle.`;
    if (first === "PAST_ENTRY_CUTOFF") return `The entry cutoff (${s.entry_cutoff || "—"}) has passed — only open trades are managed now.`;
    if (first === "KILL_SWITCH") return "The kill switch is ON — switch it off on the Autotrader page before anything can trade.";
    if (first === "EXPIRY_TAIL") return "Expiry afternoon — no new buys today; the rules sit out and trade next week's contract tomorrow.";
    if (first === "NO_CHAIN") return "No live option chain — the broker session or the chain feed is down.";
    return `${blockerWords(first).replace(/^./, (c) => c.toUpperCase())}.`;
  }
  if (edge?.verdict?.edge === false) {
    const em = edge?.expected_move?.blend_rest;
    const need = [edge?.call?.breakeven_spot_move, edge?.put?.breakeven_spot_move].filter(isNum).map(Number);
    const cheapest = need.length ? Math.min(...need) : null;
    return isNum(em) && cheapest != null
      ? `The expected move (±${num(em, 0)} pts) has to clear the cheaper side's costs (${pts(cheapest)}) — wait for volatility to expand or premium to cheapen.`
      : "The expected move has to clear the premium's costs — wait for volatility to expand or premium to cheapen.";
  }
  const lean = view?.direction === "BULLISH" ? "CALL" : view?.direction === "BEARISH" ? "PUT" : null;
  const checks = view?.canonicalChecks || {};
  if (!lean) return "No side yet. Price needs to pick a direction against VWAP with EMA alignment before any buy is valid.";
  if (checks.price_trend === false) return `Price is leaning ${lean}, but EMA 9/21, VWAP, supertrend and RSI are not all aligned that way yet.`;
  if (checks.option_chain === false) return `Price is aligned for a ${lean}; waiting for the ATM ${lean === "CALL" ? "CE" : "PE"} strikes to show buildup or premium strength.`;
  return `Trend and chain agree on a ${lean}; the quality score is still under the bar (${view?.setupScore ?? 0} of 60).`;
}

/* ── the rule's exits on the ticket ─────────────────────────────────────── */

/* The ACTIVE rule that trades this index and side, with its exit plan. */
export function ruleForSide(rules, underlying, side) {
  const want = side === "CE" ? "CALL" : side === "PE" ? "PUT" : side;
  return (rules || []).find((r) => r && r.id != null && !r.error && (!r.underlying || r.underlying === underlying)
    && r.exits?.[want] && (r.trade_direction == null || r.trade_direction === "both"
      || (r.trade_direction === "long_only" && want === "CALL") || (r.trade_direction === "short_only" && want === "PUT"))) || null;
}

/* Re-price the ticket's exits with the rule's own plan: stop = −sl%, no fixed
 * target (hold to square-off), break-even lock, size = the rule's lots. The
 * direction engine's entry limit, strike and costs are kept. */
export function applyRuleExits(plan, rule, lotSize) {
  if (!plan || !rule) return plan;
  const side = plan.optionType === "PE" ? "PUT" : "CALL";
  const ex = rule.exits?.[side];
  if (!ex || !isNum(ex.sl_pct)) return plan;
  const entry = Number(plan.entry) || 0;
  const sl = Number(ex.sl_pct);
  const tgt = Number(ex.target_pct || 0);
  const lots = rule.contract?.fixed_lots > 0 ? rule.contract.fixed_lots : (plan.lots || 1);
  const lot = Number(lotSize || plan.lotSize) || null;
  const quantity = lot ? lots * lot : plan.quantity;
  const stop = entry ? Number((entry * (1 - sl / 100)).toFixed(2)) : plan.stop;
  const riskRupees = entry && quantity ? Math.round((entry - stop) * quantity) : plan.riskRupees;
  const beArm = isNum(ex.breakeven_after_pct) && Number(ex.breakeven_after_pct) > 0 ? Number(ex.breakeven_after_pct) : null;
  const beLock = beArm != null ? Number(ex.lock_profit_pct || 0) : null;
  return {
    ...plan,
    stop, slPct: sl,
    target1: tgt > 0 && entry ? Number((entry * (1 + tgt / 100)).toFixed(2)) : null,
    target2: null, targetPct: tgt > 0 ? tgt : null,
    rewardRisk1: null, rewardRisk2: null, netRewardRisk1: null,
    lots, quantity,
    riskRupees, netRiskRupees: null,
    timeStop: null,
    rule: { id: rule.id, name: rule.name, sl, beArm, beLock, trail: ex.trailing_enabled ? { arm: ex.trail_activation_pct, pct: ex.trail_pct } : null,
            squareOff: ex.eod_squareoff || rule.window?.eod_squareoff || null, hold: !(tgt > 0) },
  };
}

/* ── bodies ──────────────────────────────────────────────────────────────── */

/* When it IS a go: contract, price limit, stop, size, money at risk, and the exit plan. */
export function OrderTicket({ plan, underlying, tone }) {
  const ink = INK[tone] || INK.slate;
  const r = plan.rule || null;
  const cells = [
    ["Buy at or below", `₹${num(plan.entry, 1)}`, ink],
    ["Stop", `₹${num(plan.stop, 1)}${r ? ` (−${num(r.sl, 0)}%)` : ""}`, "text-rose-300"],
    r?.hold
      ? ["Target", `hold to ${r.squareOff || "square-off"}`, "text-emerald-300"]
      : ["Target", plan.target1 != null ? `₹${num(plan.target1, 1)}` : "—", "text-emerald-300"],
    ["Size", plan.quantity != null ? `${plan.lots} lot${plan.lots === 1 ? "" : "s"} · ${plan.quantity}` : "—", "text-hi"],
    ["Money at risk", plan.netRiskRupees != null || plan.riskRupees != null ? `₹${num(plan.netRiskRupees ?? plan.riskRupees)}` : "—", "text-amber-300"],
  ];
  if (!r && plan.target2 != null) cells.splice(3, 0, ["Target 2", `₹${num(plan.target2, 1)}`, "text-emerald-300/80"]);
  const rr = r ? null : (plan.netRewardRisk1 ?? plan.rewardRisk1);
  return (
    <div className="min-w-0" data-testid="order-ticket">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-[11px] font-bold uppercase tracking-eyebrow text-low">Order ticket</span>
        <span className="font-mono text-lg font-black tracking-tight text-hi">{underlying} {num(plan.strike)} {plan.optionType}</span>
        {rr != null && <span className={`rounded-md border border-line-strong bg-surface px-1.5 py-0.5 text-[11px] font-black ${ink}`}>reward {num(rr, 1)}× the risk</span>}
        {plan.breakevenPremium != null && <span className="text-[11px] text-low">needs ₹{num(plan.breakevenPremium, 1)} to cover charges</span>}
      </div>
      <div className={`mt-2 grid grid-cols-2 gap-1.5 sm:grid-cols-${Math.min(cells.length, 6)}`}>
        {cells.map(([label, value, cls]) => (
          <div key={label} className="rounded-lg border border-line bg-surface px-2 py-1.5">
            <p className="text-[11px] font-bold uppercase tracking-wider text-low">{label}</p>
            <p className={`whitespace-nowrap text-sm font-black tabular-nums ${cls}`}>{value}</p>
          </div>
        ))}
      </div>
      {r ? (
        <p className="mt-1.5 text-[12px] leading-5 text-mid" data-testid="ticket-exit-plan">
          Exits as rule <b className="text-hi">{r.name}</b>: stop −{num(r.sl, 0)}%
          {r.beArm != null && <>, once up {num(r.beArm, 0)}% the stop moves to {r.beLock > 0 ? `+${num(r.beLock, 0)}%` : "break-even"}</>}
          {r.trail && <>, trail {num(r.trail.pct, 0)}% after +{num(r.trail.arm, 0)}%</>}
          {r.hold ? <>, otherwise hold to {r.squareOff || "square-off"} — no fixed target, no time stop.</> : "."}
        </p>
      ) : (
        (plan.spotInvalidation || plan.timeStop) && (
          <p className="mt-1.5 text-[12px] text-low">
            {plan.spotInvalidation ? <>Idea is wrong if spot crosses <b className="text-rose-300">{num(plan.spotInvalidation)}</b>. </> : null}
            {plan.timeStop}
          </p>
        )
      )}
    </div>
  );
}

/* When it is NOT a go: the reasons (merged) and the one thing that flips it. */
export function WhyNot({ reasons, step }) {
  return (
    <div className="min-w-0">
      {reasons.length > 0 && (
        <>
          <p className="text-[11px] font-bold uppercase tracking-eyebrow text-low">Why no trade right now</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {reasons.slice(0, 6).map((r) => (
              <span key={r.text} className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[12px] font-semibold ${r.tone === "rose"
                ? "border-rose-500/25 bg-rose-500/[.08] text-rose-200" : "border-amber-500/25 bg-amber-500/[.08] text-amber-200"}`}>
                <AlertTriangle className="h-3 w-3 shrink-0" />{r.text}
              </span>
            ))}
          </div>
        </>
      )}
      <div className={`${reasons.length ? "mt-2.5" : ""} rounded-lg border border-cyan-500/25 bg-cyan-500/[.06] px-2.5 py-2`}>
        <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-eyebrow text-cyan-300">
          <Eye className="h-3 w-3" />What needs to change
        </p>
        <p className="mt-1 text-[13px] leading-5 text-mid">{step}</p>
      </div>
    </div>
  );
}

/* ── rule watch ──────────────────────────────────────────────────────────── */

const LEG_WORDS = {
  close: "price", vwap: "today's average (VWAP)", session_rsi: "session RSI", adx: "trend strength (ADX)",
  stoch_d: "stochastic", bb_width: "band width", atr_pct: "movement (ATR)", skw_day: "put/call skew drift",
  max_drawdown_20: "pullback from the 20-bar high",
};
const legWord = (k) => LEG_WORDS[k] || words(k);

/* One missing condition in plain words, with how far it is. */
export function missingLegText(leg) {
  if (!leg) return "";
  const l = legWord(leg.left);
  if (leg.op === "cross_up" || leg.op === "cross_down") {
    const gap = isNum(leg.gap) ? Number(leg.gap) : null;
    const r = legWord(leg.right);
    if (gap == null) return `${l} has not crossed ${leg.op === "cross_up" ? "above" : "below"} ${r}`;
    const side = gap >= 0 ? "above" : "below";
    return `${l} is ${num(Math.abs(gap), Math.abs(gap) < 10 ? 1 : 0)} pts ${side} ${r} (needs a cross ${leg.op === "cross_up" ? "up through" : "down through"} it)`;
  }
  const rv = isNum(leg.right_value) ? Number(leg.right_value) : null;
  const lv = isNum(leg.left_value) ? Number(leg.left_value) : null;
  const dir = leg.op === "gte" || leg.op === "gt" ? "at least" : leg.op === "lte" || leg.op === "lt" ? "at most" : leg.op_label || leg.op;
  const fmt = (v) => (Math.abs(v) < 1 ? num(v, 4) : Number.isInteger(v) || Math.abs(v) >= 100 ? num(v, 0) : num(v, 1));
  if (lv == null || rv == null) return `${l} ${dir} ${leg.right}`;
  return `${l} ${fmt(lv)}, needs ${dir} ${fmt(rv)}`;
}

/* How close each side of a rule is to firing, as a plain reading. */
export function ruleWatch(rule) {
  if (!rule || rule.error || !rule.ready) return null;
  const sides = ["CALL", "PUT"].map((s) => ({ side: s, ...(rule.ready[s] || { passed: 0, total: 0, missing: [] }) }))
    .filter((s) => s.total > 0)
    .filter((s) => rule.trade_direction == null || rule.trade_direction === "both"
      || (rule.trade_direction === "long_only" && s.side === "CALL") || (rule.trade_direction === "short_only" && s.side === "PUT"));
  if (!sides.length) return null;
  const fired = rule.side ? sides.find((s) => s.side === rule.side) : null;
  const nearest = fired || sides.slice().sort((a, b) => (b.passed / b.total) - (a.passed / a.total) || b.passed - a.passed)[0];
  const cap = Number(rule.max_trades_per_day || 0);
  const used = Number(rule.day_trades || 0);
  const left = cap > 0 ? Math.max(0, cap - used) : null;
  return {
    side: nearest.side, fired: Boolean(fired), passed: nearest.passed, total: nearest.total,
    missing: (nearest.missing || []).map(missingLegText).filter(Boolean),
    tradesLeft: left, tradesUsed: used, cap,
    window: rule.window || null, bar: rule.bar || null,
  };
}

/* What each ACTIVE rule reads right now — and how close it is to a trade. */
export function RulesRow({ rules, underlying }) {
  const engine = useEngineState();   // armed / paused / halted, from the shell's status poll
  if (!rules?.length) return <span className="text-[12px] text-slate-500">No ACTIVE rules for {underlying} — nothing is trading this index.</span>;
  return (
    <span className="flex min-w-0 flex-col gap-1.5" data-testid="rule-watch">
      {rules.map((r, i) => {
        const side = r.side;
        const w = ruleWatch(r);
        const cls = side === "CALL" ? "border-emerald-500/35 bg-emerald-500/[.10] text-emerald-200"
          : side === "PUT" ? "border-rose-500/35 bg-rose-500/[.10] text-rose-200" : "border-line-strong bg-surface-3 text-slate-300";
        const Icon = side === "CALL" ? ArrowUpRight : side === "PUT" ? ArrowDownRight : Minus;
        return (
          <span key={r.id ?? `r${i}`} className={`flex min-w-0 flex-col gap-0.5 rounded-lg border px-2.5 py-1.5 text-[12px] ${cls}`}
            title={r.timeframe ? `timeframe ${String(r.timeframe).replace("minute", "m")}${w?.bar ? ` · last closed bar ${w.bar}` : ""}` : ""}>
            <span className="flex flex-wrap items-center gap-1.5">
              <EngineStateTag state={engine} />
              <Icon className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate font-bold">{r.name}</span>
              <span className="shrink-0 rounded bg-slate-950/40 px-1.5 py-px text-[11px] font-black uppercase tracking-wider">
                {side ? `${side} signal` : w ? `${w.passed} of ${w.total} for ${w.side}` : "waiting"}
              </span>
              {w?.tradesLeft != null && <span className="shrink-0 text-[11px] opacity-80">{w.tradesLeft} of {w.cap} trade{w.cap === 1 ? "" : "s"} left today</span>}
              {r.error && <span className="shrink-0 text-[11px] text-slate-500">{words(r.error)}</span>}
            </span>
            {w && !side && w.missing.length > 0 && (
              <span className="text-[12px] text-slate-400">Missing: {w.missing.slice(0, 2).join("; ")}{w.missing.length > 2 ? "; …" : ""}</span>
            )}
          </span>
        );
      })}
    </span>
  );
}
